'use client'

import { useState, useEffect, useContext } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Badge } from '@/components/ui/shadcn/badge'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Separator } from '@/components/ui/shadcn/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/shadcn/dialog'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertTriangle } from 'lucide-react'
import ErrorAlerts from '../feedback/ErrorAlerts'
import { SettingsContext } from '@/contexts/SettingsContext'
import { AuthContext } from '@/contexts/AuthContext'
import {
  COLOR_PALETTE,
  getDefaultColor,
  getNextAvailableColor,
} from '@/lib/utils/colors'
import { cn } from '@/lib/utils'

const ColorPicker = ({ selectedColor, onColorChange, disabled, name }) => {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Group Color</p>
      <div className="flex flex-wrap gap-2">
        {COLOR_PALETTE.map(color => (
          <button
            type="button"
            key={color.primary}
            onClick={() => !disabled && onColorChange(color.primary)}
            disabled={disabled}
            title={color.name}
            className={cn(
              'size-8 rounded-full border-2 transition-all',
              selectedColor === color.primary
                ? 'border-foreground ring-2 ring-ring'
                : 'border-border hover:scale-110',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            style={{ backgroundColor: color.primary }}
          />
        ))}
      </div>
      {selectedColor && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Preview:</span>
          <Badge
            variant="outline"
            style={{
              backgroundColor: selectedColor,
              color:
                COLOR_PALETTE.find(c => c.primary === selectedColor)
                  ?.contrastText || '#ffffff',
              borderColor: selectedColor,
            }}
          >
            {name || 'Group Name'}
          </Badge>
        </div>
      )}
    </div>
  )
}

export default function CreateUserGroupModal({
  open,
  onClose,
  loading,
  submit,
  error,
  errors,
  edit,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [roles, setRoles] = useState([])
  const [selectedColor, setSelectedColor] = useState(getDefaultColor().primary)
  const settingsContext = useContext(SettingsContext)
  const authContext = useContext(AuthContext)

  const canEdit = authContext.user.isAuthorized('editUserGroups')

  useEffect(() => {
    if (!open && isOpen) {
      setName('')
      setSelectedColor(getDefaultColor().primary)
    }
    setIsOpen(open)
  }, [open, isOpen])

  useEffect(() => {
    if (edit) {
      setName(edit.name)
      setRoles(edit.roles || [])
      setSelectedColor(edit.color || getDefaultColor().primary)
    } else {
      setName('')
      setRoles([])
      const usedColors = settingsContext.userGroups.map(group => group.color)
      setSelectedColor(getNextAvailableColor(usedColors).primary)
    }
  }, [edit, settingsContext.userGroups])

  const toggleRole = roleId => {
    setRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(r => r !== roleId)
        : [...prev, roleId],
    )
  }

  const title = edit
    ? canEdit
      ? 'Edit User Group'
      : 'View User Group'
    : 'Create User Group'

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose()
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure a user group
          </DialogDescription>
        </DialogHeader>

        <ErrorAlerts
          error={error}
          errors={errors}
          message="Something went wrong with your submission:"
        />

        <form onSubmit={e => submit(e, edit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="usergroup-name">Name</Label>
            <Input
              id="usergroup-name"
              name="name"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!canEdit}
              required
            />
          </div>

          <div className="rounded-md border bg-muted/40 p-3">
            <ColorPicker
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
              disabled={!canEdit}
              name={name}
            />
            <input type="hidden" name="color" value={selectedColor} />
          </div>

          <div className="space-y-2">
            <Label>Choose one or more Roles</Label>
            {!settingsContext.roles.length ? (
              <Alert>
                <AlertTriangle className="size-4" />
                <AlertDescription>
                  No roles have been created. Create a role by closing this
                  modal, and clicking <b>Create Role</b> in the roles section of
                  settings.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="rounded-md border p-3 space-y-2 max-h-48 overflow-y-auto">
                {settingsContext.roles.map(r => {
                  const checked = roles.includes(r.id)
                  return (
                    <div key={r.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`role-${r.id}`}
                        checked={checked}
                        disabled={!canEdit}
                        onCheckedChange={() => toggleRole(r.id)}
                      />
                      <Label
                        htmlFor={`role-${r.id}`}
                        className="cursor-pointer font-normal"
                      >
                        {r.name}
                      </Label>
                    </div>
                  )
                })}
              </div>
            )}
            <input type="hidden" name="roles" value={JSON.stringify(roles)} />
          </div>

          <Separator />

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="submit"
              className="w-full"
              disabled={
                loading ||
                !settingsContext.roles.length ||
                !roles.length ||
                !canEdit
              }
            >
              {loading ? 'Saving...' : edit ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              {edit && !canEdit ? 'Close' : 'Cancel'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
